import { Link } from "react-router-dom";
import { AlertTriangle, Gavel, UserCog, Users } from "lucide-react";
import { CompositionPicker, compositionSummary, sectorLabel } from "./CompositionPicker";
import { BenchJudgePicker } from "./BenchJudgePicker";
import { NeedsReassignmentBadge, singleCaseReassignTarget } from "./NeedsReassignmentBadge";
import { Button } from "../components/ui/button";
import { DocketButton, docketKey } from "./DocketButton";

// «537 - ΕΝΕΜ7 (ΠΟΛ)» → «ΕΝΕΜ7 (ΠΟΛ)» (Τμήμα = όνομα πινακίου στο SOLON TrackDocket)
const tmimaOf = (label) => { const parts = String(label || "").split(" - "); return parts.length > 1 ? parts.slice(1).join(" - ").trim() : String(label || "").trim(); };

const isPolyEntry = (e) => e.court_type === "Πολυμελές" || /\(ΠΟΛ\)/i.test(e.pinakio_label || "") || !!e.composition;
const calPinakio = (e) => ({ pinakio_code: `cal-${String(e.pinakio_label || "").replace(/\s+/g, "")}`, label: e.pinakio_label, court_type: e.court_type });

// Απευθείας ενέργειες από το Ημερολόγιο Έδρας: Μονομελές → Δικαστής Έδρας · Πολυμελές → Σύνθεση + Δικαστής Έδρας · ΑΝΑΧΡΕΩΣΗ όταν υπάρχει δικαστής προδικαστικού
const BenchEntryActions = ({ e, judgesByCt, onReload, onReassign }) => {
  const pinakio = calPinakio(e);
  const ct = isPolyEntry(e) ? "Πολυμελές" : (e.court_type || "Μονομελές");
  const benchGroup = {
    date: e.hearing_date, bench_judge: e.unassigned || e.is_composition ? "" : e.judge, bench_judges: [],
    pretrial_judges: e.pretrial_judges || [],
    pretrial_judge_suggestion: (e.pretrial_judges || []).length === 1 ? e.pretrial_judges[0] : "",
  };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`bench-entry-actions-${e.hearing_date}-${e.pinakio_label}`}>
      {isPolyEntry(e) && (
        <CompositionPicker pinakio={pinakio} dateGroup={{ date: e.hearing_date, composition: e.composition }} hideBadge onDone={() => onReload?.()} />
      )}
      <BenchJudgePicker pinakio={{ ...pinakio, court_type: ct }} dateGroup={benchGroup} judges={judgesByCt?.[ct] || []} hideBadge onDone={() => onReload?.()} />
      {(e.pretrial_judges || []).length > 0 && onReassign && (
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-orange-700 hover:text-orange-900"
                onClick={() => onReassign({ pinakioLabel: e.pinakio_label, hearingDate: e.hearing_date, courtType: ct })}
                title="ΑΝΑΧΡΕΩΣΗ: αντικατάσταση δικαστή (προδικαστικού + έδρας) + Πράξεις Νέου Ορισμού (ZIP)"
                data-testid={`bench-reassign-${pinakio.pinakio_code}-${e.hearing_date}`}>
          <UserCog className="w-3.5 h-3.5" />ΑΝΑΧΡΕΩΣΗ
        </Button>
      )}
    </div>
  );
};

const DAY_NAMES = ["Δευ", "Τρι", "Τετ", "Πεμ", "Παρ", "Σαβ", "Κυρ"];

const buildWeeks = (year, month) => {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const iso = (year, month, day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

// Μηνιαίο πλέγμα: δικάσιμοι με Δικαστή Έδρας (κεχριμπαρί) + δικάσιμοι ΧΩΡΙΣ δικαστή (κόκκινο περίγραμμα)
export const BenchMonthGrid = ({ year, month, entries, showJudge, selectedDate, onSelectDate }) => {
  const byDate = entries.reduce((acc, e) => { (acc[e.hearing_date] ||= []).push(e); return acc; }, {});
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="bench-month-grid">
      <div className="grid grid-cols-7 bg-slate-800 text-slate-200 text-[11px] font-medium uppercase tracking-wider">
        {DAY_NAMES.map(d => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      {buildWeeks(year, month).map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-t">
          {week.map((day, di) => {
            if (!day) return <div key={di} className="min-h-[92px] bg-slate-50/60 border-r last:border-r-0" />;
            const dateIso = iso(year, month, day);
            const dayEntries = byDate[dateIso] || [];
            const assigned = dayEntries.filter(e => !e.unassigned);
            const unassigned = dayEntries.filter(e => e.unassigned);
            const isSel = selectedDate === dateIso;
            const isToday = dateIso === today;
            const base = isSel ? "bg-amber-100" : dayEntries.length ? "bg-amber-50/60 hover:bg-amber-100/70" : "hover:bg-slate-50";
            const redRing = unassigned.length ? "shadow-[inset_0_0_0_2px_#dc2626]" : "";
            return (
              <button key={di} type="button"
                      onClick={() => onSelectDate(dayEntries.length ? dateIso : null)}
                      className={`min-h-[92px] text-left p-1.5 border-r last:border-r-0 transition-colors ${base} ${redRing}`}
                      data-testid={`bench-day-${dateIso}`}
                      data-unassigned={unassigned.length}>
                <div className={`text-xs font-semibold mb-1 ${isToday ? "text-blue-700" : dayEntries.length ? "text-amber-900" : "text-slate-400"}`}>{day}</div>
                <div className="space-y-0.5">
                  {assigned.slice(0, 3).map((e, i) => (
                    <div key={i} className={`text-[10px] leading-tight rounded bg-white border px-1 py-0.5 truncate flex items-center gap-1 ${e.is_composition ? "border-violet-400" : "border-amber-300"}`}
                         title={`${e.pinakio_label} · ${e.case_count} υποθ. · ${e.is_composition ? `Σύνθεση ${e.composition?.label}: ${compositionSummary(e.composition)}` : e.judge}`}
                         data-testid={e.is_composition ? `bench-chip-composition-${e.hearing_date}-${e.pinakio_label}` : undefined}>
                      {e.is_composition ? <Users className="w-2.5 h-2.5 text-violet-700 shrink-0" /> : <Gavel className="w-2.5 h-2.5 text-amber-700 shrink-0" />}
                      <span className="font-mono truncate">{e.pinakio_label}</span>
                      <span className="text-slate-500 shrink-0">{e.case_count}</span>
                    </div>
                  ))}
                  {assigned.length > 3 && <div className="text-[10px] text-amber-800">+{assigned.length - 3} ακόμη</div>}
                  {showJudge && assigned.length > 0 && (
                    <div className="text-[10px] text-slate-600 truncate">{[...new Set(assigned.map(e => e.is_composition ? `Σύνθεση ${e.composition?.label}` : e.judge))].join(", ")}</div>
                  )}
                  {unassigned.length > 0 && (
                    <div className="text-[10px] leading-tight rounded border-2 border-red-600 bg-red-50 text-red-700 font-semibold px-1 py-0.5 flex items-center gap-1"
                         title={unassigned.map(e => `${e.pinakio_label} (${e.case_count})`).join(", ")}
                         data-testid={`bench-day-unassigned-${dateIso}`}>
                      <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                      {unassigned.length === 1 ? <span className="font-mono truncate">{unassigned[0].pinakio_label}</span> : <span>{unassigned.length} πινάκια</span>}
                      <span className="shrink-0 font-normal">χωρίς δικαστή</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Λίστα δικασίμων (ανά ημέρα ή όλος ο μήνας) με drill-down σε υποθέσεις — κόκκινο περίγραμμα όταν λείπει δικαστής
// canAssign → inline «Δικαστής Έδρας» (Μονομελές) / «Ανάθεση σε Σύνθεση» + «Δικαστής Έδρας» (Πολυμελές)
export const BenchEntriesList = ({ entries, privacyFn, canAssign = false, judgesByCt, onReload, onReassign, docketMap = {}, onDocketFetched }) => {
  if (!entries.length) return <p className="text-sm text-slate-500 italic" data-testid="bench-entries-empty">Καμία δικάσιμος για την επιλογή.</p>;
  const fmt = (d) => { const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; };
  return (
    <div className="space-y-3" data-testid="bench-entries-list">
      {entries.map((e, i) => (
        <div key={i}
             className={`rounded-lg bg-white overflow-hidden ${e.unassigned ? "border-2 border-red-600" : "border"}`}
             data-testid={`bench-entry-${e.hearing_date}-${e.pinakio_label}`}
             data-unassigned={e.unassigned ? "1" : "0"}>
          <div className={`flex flex-wrap items-center gap-3 px-4 py-2 border-b ${e.unassigned ? "bg-red-50" : e.is_composition ? "bg-violet-50" : "bg-amber-50"}`}>
            <span className="font-semibold text-sm text-slate-800">{fmt(e.hearing_date)}</span>
            <span className={`font-mono text-sm ${e.unassigned ? "text-red-800" : e.is_composition ? "text-violet-900" : "text-amber-900"}`}>{e.pinakio_label}</span>
            <span className="text-xs text-slate-500">{e.court_type}</span>
            {e.unassigned ? (
              <span className="text-xs font-semibold text-red-700 flex items-center gap-1" data-testid={`bench-entry-unassigned-badge-${e.hearing_date}-${e.pinakio_label}`}>
                <AlertTriangle className="w-3.5 h-3.5" />{isPolyEntry(e) ? "ΧΩΡΙΣ ΣΥΝΘΕΣΗ / ΔΙΚΑΣΤΗ ΕΔΡΑΣ" : "ΧΩΡΙΣ ΔΙΚΑΣΤΗ ΕΔΡΑΣ"}
                {!canAssign && <Link to="/hearings" className="ml-1 underline font-normal text-red-700 hover:text-red-900">Ορισμός στα Πινάκια</Link>}
              </span>
            ) : e.is_composition ? (
              <span className="text-xs text-violet-900 flex items-center gap-1 flex-wrap" data-testid={`bench-entry-composition-${e.hearing_date}-${e.pinakio_label}`}>
                <Users className="w-3.5 h-3.5 text-violet-700" /><b>Σύνθεση «{e.composition?.label}»</b>
                {sectorLabel(e.composition) && <span className="text-violet-800">— {sectorLabel(e.composition)}</span>}
                <span className="text-slate-700">{compositionSummary(e.composition)}</span>
              </span>
            ) : (
              <span className="text-xs text-slate-600 flex items-center gap-1"><Gavel className="w-3 h-3 text-amber-700" />{e.judge}</span>
            )}
            <span className="ml-auto text-xs font-medium text-slate-700">{e.case_count} υποθ.</span>
            <DocketButton pinakioName={tmimaOf(e.pinakio_label)} pinakioCode={calPinakio(e).pinakio_code} date={e.hearing_date}
                          entry={docketMap[docketKey(tmimaOf(e.pinakio_label), e.hearing_date)]} onFetched={onDocketFetched} />
            {e.needs_reassignment_count > 0 && (
              <span className="inline-flex items-center rounded bg-red-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 tracking-wide"
                    title="Υποθέσεις όπου ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ" data-testid={`bench-needs-reassignment-count-${e.hearing_date}-${e.pinakio_label}`}>
                {e.needs_reassignment_count} ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ
              </span>
            )}
            {!e.same_as_pretrial && e.pretrial_judges.length > 0 && (
              <span className="text-[10px] text-orange-700" title="Δικαστής προδικαστικού ελέγχου">Προδ.: {e.pretrial_judges.join(", ")}</span>
            )}
          </div>
          {canAssign && (
            <div className={`flex flex-wrap items-center gap-2 px-4 py-1.5 border-b text-xs ${e.unassigned ? "bg-red-50/40" : "bg-slate-50"}`}
                 data-testid={`bench-entry-actions-row-${e.hearing_date}-${e.pinakio_label}`}>
              <span className="text-slate-500">{isPolyEntry(e) ? "Πολυμελές: Σύνθεση & Δικαστής Έδρας" : "Μονομελές: Δικαστής Έδρας"}{(e.pretrial_judges || []).length > 0 ? " · ΑΝΑΧΡΕΩΣΗ" : ""}</span>
              <div className="ml-auto"><BenchEntryActions e={e} judgesByCt={judgesByCt} onReload={onReload} onReassign={onReassign} /></div>
            </div>
          )}
          <div className="divide-y">
            {e.cases.map(c => (
              <div key={c.id} className="grid grid-cols-[110px_90px_1fr_auto] gap-2 px-4 py-1.5 text-xs items-center" data-testid={`bench-case-${c.id}`}>
                <Link to={`/cases/${c.id}`} className="font-mono font-semibold text-primary hover:underline">{c.gak}</Link>
                <span className="font-mono text-slate-600">{c.eak || "—"}</span>
                <span className="text-slate-600 truncate">{privacyFn ? privacyFn(c.subject) : c.subject}</span>
                <NeedsReassignmentBadge need={c.needs_reassignment} caseId={c.id} compact
                  onClick={onReassign ? () => onReassign(singleCaseReassignTarget({
                    pinakioLabel: e.pinakio_label, hearingDate: e.hearing_date, courtType: isPolyEntry(e) ? "Πολυμελές" : (c.court_type || e.court_type || "Μονομελές"),
                    caseId: c.id, benchJudge: c.bench_judge })) : undefined} />
              </div>
            ))}
            {e.case_count > e.cases.length && <div className="px-4 py-1.5 text-[11px] text-slate-500 italic">… και {e.case_count - e.cases.length} ακόμη — δείτε το πινάκιο στη σελίδα Πινάκια</div>}
          </div>
        </div>
      ))}
    </div>
  );
};
